import { common, enums, resources } from '../gads'
import { GadsFieldsRequired } from '../utils/global'
import { Account, Domain, Targets, Improvement, Assets } from '..'
import { PolicyTopicEntryTypeName } from '../assets'
import { IntegrationsScoreSection } from './integrations'
import { RsaScoreSection } from './rsa'
import { ProductPerformanceScoreSection } from './productPerformance'

export interface LineChartSeries {
    name: string
    items: LineChartItem[]
    dotted?: boolean
    color?: ChartColors
}

export enum ChartColors {
    'opteo-blue' = 0,
    'opteo-purple' = 1,
    'opteo-teal' = 2,
    'opteo-pink' = 3,
    'opteo-amber' = 4,
    'opteo-orange' = 5,
}

// The order of these items matters. See this for the desired order: https://www.notion.so/opteo/Scorecard-Order-9be462d67b5447f6bd87ebcb21fb4044
export const SectionTypes = {
    integrations: 'integrations',
    conversionTracking: 'conversionTracking',
    disapprovals: 'disapprovals',
    landingPages: 'landingPages',
    projectedSpend: 'projectedSpend',
    budgetCap: 'budgetCap',
    spendAllocation: 'spendAllocation',
    // Search campaigns here
    qs: 'qs',
    rsa: 'rsa',
    adExtension: 'adExtension',
    matchtype: 'matchtype',
    sqr: 'sqr',
    searchTermAlignment: 'searchTermAlignment',
    performanceMax: 'performanceMax',
    productPerformance: 'productPerformance',
} as const

export const SlideOrder = Object.keys(SectionTypes)

export const sectionTypeIncCover = ['cover', 'overview', ...Object.keys(SectionTypes)] as const

export type SectionType = (typeof SectionTypes)[keyof typeof SectionTypes]
export type SectionTypeIncCover =
    | (typeof SectionTypes)[keyof typeof SectionTypes]
    | 'cover'
    | 'overview'

export const SectionNames = {
    [SectionTypes.sqr]: 'Search Term Coverage',
    [SectionTypes.projectedSpend]: 'Projected Spend',
    [SectionTypes.qs]: 'Quality Score',
    [SectionTypes.budgetCap]: 'Budget Limited Campaigns',
    [SectionTypes.matchtype]: 'Match Type Breakdown',
    [SectionTypes.conversionTracking]: 'Conversion Tracking',
    [SectionTypes.adExtension]: 'Ad Extension Coverage',
    [SectionTypes.productPerformance]: 'Product Performance',
    [SectionTypes.performanceMax]: 'Performance Max',
    [SectionTypes.landingPages]: 'Landing Pages',
    [SectionTypes.disapprovals]: 'Disapprovals',
    [SectionTypes.integrations]: 'Integrations',
    [SectionTypes.rsa]: 'Responsive Search Ads',
    [SectionTypes.searchTermAlignment]: 'Search Term Alignment',
    [SectionTypes.spendAllocation]: 'Campaign Spend',
} as const // satisfies Record<SectionType, string>

export interface Section {
    type: SectionType
    invalid: boolean
    score: number
}

interface ConvMetricsTableRow {
    name: string
    metrics: {
        cost: number
        conversions: number
        conversions_value: number
    }
}

interface PieDataRow {
    name: string
    y: number
}
export interface ProjectedSpendScoreKeyStats {
    spend_to_date: number
    projected_spend: number
    monthly_budget: number
    remaining_budget: number
    current_daily_budget: number
    suggested_daily_budget: number
    average_daily_spend?: number // required for new scorecards, but old scorecards may not not have this defined
}

export interface ProjectedSpendScoreDetails {
    series: LineChartSeries[]
    spend_status: SpendStatus
    budget: number
    projected_final_day_cost: number
    key_stats: ProjectedSpendScoreKeyStats
}

export interface ProjectedSpendScoreSection extends Section {
    type: 'projectedSpend'
    details: ProjectedSpendScoreDetails
}

export enum PerformanceMaxChannels {
    VIDEO = 'Video',
    DISPLAY = 'Display',
    SHOPPING = 'Shopping',
    OTHER = 'Other',
}

//  Performance Max
export const ValidAssetTypes = [
    enums.AssetFieldType.HEADLINE,
    enums.AssetFieldType.LONG_HEADLINE,
    enums.AssetFieldType.DESCRIPTION,
    enums.AssetFieldType.BUSINESS_NAME,
    enums.AssetFieldType.MARKETING_IMAGE,
    enums.AssetFieldType.SQUARE_MARKETING_IMAGE,
    enums.AssetFieldType.PORTRAIT_MARKETING_IMAGE,
    enums.AssetFieldType.LOGO,
    enums.AssetFieldType.LANDSCAPE_LOGO,
    enums.AssetFieldType.YOUTUBE_VIDEO,
    enums.AssetFieldType.CALL_TO_ACTION_SELECTION,
    enums.AssetFieldType.MEDIA_BUNDLE,
] as const
export type ValidAssetType = (typeof ValidAssetTypes)[number]

export const ValidAssetTypeInfoRel = {
    [enums.AssetFieldType.HEADLINE]: {
        formattedAssetType: 'Headline',
        recommendedAssetCount: 5,
    },
    [enums.AssetFieldType.LONG_HEADLINE]: {
        formattedAssetType: 'Long Headline',
        recommendedAssetCount: 2,
    },
    [enums.AssetFieldType.DESCRIPTION]: {
        formattedAssetType: 'Description',
        recommendedAssetCount: 3,
    },
    [enums.AssetFieldType.BUSINESS_NAME]: {
        formattedAssetType: 'Business Name',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.MARKETING_IMAGE]: {
        formattedAssetType: 'Marketing Image',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.SQUARE_MARKETING_IMAGE]: {
        formattedAssetType: 'Sq. Marketing Image',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.PORTRAIT_MARKETING_IMAGE]: {
        formattedAssetType: 'Portrait Image',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.LOGO]: {
        formattedAssetType: 'Logo',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.LANDSCAPE_LOGO]: {
        formattedAssetType: 'Landscape Logo',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.YOUTUBE_VIDEO]: {
        formattedAssetType: 'YouTube Video',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.CALL_TO_ACTION_SELECTION]: {
        formattedAssetType: 'Call To Action',
        recommendedAssetCount: 1,
    },
    [enums.AssetFieldType.MEDIA_BUNDLE]: {
        formattedAssetType: 'Media Bundle',
        recommendedAssetCount: 0,
    },
    unknownAssetType: {
        formattedAssetType: 'Unknown Asset Type',
        recommendedAssetCount: 0,
    },
} as const

export const FormattedAssetTypes = Object.values(ValidAssetTypeInfoRel).map(
    ({ formattedAssetType }) => formattedAssetType
)
type FormattedAssetType = (typeof FormattedAssetTypes)[number]

export interface ChannelDistribution {
    channel: PerformanceMaxChannels
    cost: number
    conversions: number
    cpa: number | 'Infinity'
    conversionsValue: number
    roas: number
}
export interface ChannelDistributionCampaignBreakdown extends ChannelDistribution {
    campaign: string
    resource_name: string
}

type AssetGroupName = string | null

export interface InputCoverage {
    campaign: string
    resource_name: string
    assetType: FormattedAssetType
    count: number
    recommended: number
}
export interface InputCoverageCampaignBreakdown extends InputCoverage {
    assetGroup: AssetGroupName
}

export interface AudienceSignal {
    audienceSignal: string
    usedIn: number
}
export interface AudienceSignalBreakdown {
    campaign: string
    resource_name: string
    assetGroup: AssetGroupName
    audienceSignal: string
    count: number
}

export interface PerformanceMaxScoreDetails {
    input_coverage: InputCoverage[]
    channel_distribution: ChannelDistribution[]
    audience_signal: AudienceSignal[]
    totalNumberOfAssetGroups: number
}
export interface PerformanceMaxScoreBreakdown {
    input_coverage: InputCoverageCampaignBreakdown[]
    channel_distribution: ChannelDistributionCampaignBreakdown[]
    audience_signal: AudienceSignalBreakdown[]
    totalCostPerCampaign: Record<string, number>
}

export interface PerformanceMaxScoreSection extends Section {
    type: 'performanceMax'
    details: PerformanceMaxScoreDetails
    breakdown?: PerformanceMaxScoreBreakdown
}

export interface SpendAllocationSection extends Section {
    type: typeof SectionTypes.spendAllocation
    details: {
        suggestedDailyReallocation: {
            daily: number
            percent: number
        }
        chartData: { x: number; y: number; label: string }[]
        activeCampaigns: number
        leftoverSpend: number
        avgCpa: number
        avgRoas: number
        totalSpend: number
        totalConversions: number
        totalConvValue: number
        cpaDelta: number
        roasDelta: number
        conversionsDelta: number
        conversionsValueDelta: number
        estimatedConvValueGainDaily: number
        estimatedConvGainDaily: number
        spendDelta: number
        activeCampaignsDelta: number
        estimatedConvGainDailyDelta: number
        estimatedConvValueGainDailyDelta: number
        campaignsSpendDetailsOverAllocated: CampaignsSpendMetrics[]
        campaignsSpendDetailsUnderAllocated: CampaignsSpendMetrics[]
    }
    breakdown?: { allocatedCampaignsSpend: CampaignsSpendMetrics[] }
}

export interface SqrScoreDetails {
    noSearchTerms: boolean
    pie_data: PieDataRow[]
    table_data: ConvMetricsTableRow[]
    sub_table_data: ConvMetricsTableRow[]
}

export interface SqrScoreSection extends Section {
    type: 'sqr'
    details: SqrScoreDetails
}

export interface QsScoreDetails {
    total: number
    table_data: ConvMetricsTableRow[]
    series: number[]
    factor: string
}

export interface QsCampaignBreakdown {
    campaign: string
    qualityScore: number
    cost: number
    conversions: number
    conversionsValue: number
}

export interface QsScoreSection extends Section {
    type: 'qs'
    details: QsScoreDetails
    breakdown?: QsCampaignBreakdown[]
}

export interface BudgetCapScoreDetails {
    total_cost: number
    uncapped_cost: number
    desired_ratio: number
    pie_data: PieDataRow[]
    top_capped_campaigns: {
        name: string
        cost: number
        conversions: number
        conversions_value: number
        impression_share_lost: number
    }[]
}

export interface BudgetCapScoreSection extends Section {
    type: 'budgetCap'
    details: BudgetCapScoreDetails
}

export interface MatchTypeSpreadScoreDetails {
    spread: Record<
        MatchtypeSpreadLabelKeys,
        {
            cost: number
            conversions: number
            conversions_value: number
            cpa: null | number
            roas: null | number
        }
    >
    pie_data: PieDataRow[]
    table_data: TableData[]
    desired_exact_ratio: number
    desired_non_broad_ratio: number
    exact_ratio: number
    non_broad_ratio: number
    exact_score: number
    non_broad_score: number
    total_cost: number
}

export interface MatchTypeSpreadScoreSection extends Section {
    type: 'matchtype'
    details: MatchTypeSpreadScoreDetails
}

export const ConversionTrackingMiniScore = {
    conversionVolume: 'conversionVolume',
    // TODO(scorecard): add this mini score when upgrading to Gads v14 -> importingGA4Conversions: 'importingGA4Conversions',
    conversionValues: 'conversionValues',
    dynamicConversionValues: 'dynamicConversionValues',
} as const
export type ConversionTrackingMiniScore =
    (typeof ConversionTrackingMiniScore)[keyof typeof ConversionTrackingMiniScore]

interface TopConversionAction {
    conversionName: string
    source: string
    included: string
    conversions: number
    conversionsValue: number
}

export interface ConversionTrackingScoreDetails {
    miniScores: Record<ConversionTrackingMiniScore, number>
    conversionCount: number
    desiredConversions: number
    dailyPerformanceMetrics: {
        byConversions: { date: string; conversions: number }[]
        byConversionsValue: { date: string; conversionsValue: number }[]
    }
    topConversionActions: {
        byConversions: TopConversionAction[]
        byConversionsValue: TopConversionAction[]
    }
}

export interface ConversionTrackingScoreSection extends Section {
    type: 'conversionTracking'
    details: ConversionTrackingScoreDetails
}

export interface AdExtensionScoreDetails {
    extensions: Record<string, { type: string; score: number }>
}

export interface AdExtensionScoreSection extends Section {
    type: 'adExtension'
    details: AdExtensionScoreDetails
    breakdown?: AdExtensionScoreBreakdownItem[]
}

export interface AdExtensionScoreBreakdownItem {
    adGroupRn: string
    adGroupName: string
    campaignRn: string
    campaignName: string
    cost: number
    extensions: {
        type: string
        customerLevelCount: number
        campaignLevelCount: number
        adGroupLevelCount: number
        totalCount: number
    }[]
}

export interface GoogleAnalyticsMetricDelta {
    value: number
    delta: number
}
export interface GoogleAnalyticsScoreDetails {
    bounceRate: GoogleAnalyticsMetricDelta
    averageSessionDuration: GoogleAnalyticsMetricDelta
    averagePagesPerSession: GoogleAnalyticsMetricDelta
    newSessions: GoogleAnalyticsMetricDelta
    previousPeriodBounceRatePerDay: { date: string; bounceRate: number }[]
    thisPeriodBounceRatePerDay: { date: string; bounceRate: number }[]
}

// export interface GoogleAnalyticsScoreSection extends Section {
//     type: 'ga'
//     details: GoogleAnalyticsScoreDetails
// }

export interface AllSections {
    [SectionTypes.sqr]: SqrScoreSection
    [SectionTypes.projectedSpend]: ProjectedSpendScoreSection
    [SectionTypes.qs]: QsScoreSection
    [SectionTypes.budgetCap]: BudgetCapScoreSection
    [SectionTypes.spendAllocation]: SpendAllocationSection
    [SectionTypes.matchtype]: MatchTypeSpreadScoreSection
    [SectionTypes.conversionTracking]: ConversionTrackingScoreSection
    [SectionTypes.adExtension]: AdExtensionScoreSection
    [SectionTypes.productPerformance]: ProductPerformanceScoreSection
    [SectionTypes.performanceMax]: PerformanceMaxScoreSection
    [SectionTypes.landingPages]: LandingPagesScoreSection
    [SectionTypes.disapprovals]: DisapprovalsScoreSection
    [SectionTypes.integrations]: IntegrationsScoreSection
    [SectionTypes.rsa]: RsaScoreSection
    [SectionTypes.searchTermAlignment]: SearchTermAlignmentSection
}

export const BetaSectionTypes: Partial<Record<keyof AllSections, boolean>> = {
    // [SectionTypes.landingPages]: true,
    // [SectionTypes.rsa]: true,
    // [SectionTypes.searchTermAlignment]: true,
    // [SectionTypes.spendAllocation]: true,
}

export type AllDetails = AllSections[keyof AllSections]['details']

export type PickScorecardBreakdowns<T> = {
    [K in keyof T as T[K] extends { breakdown?: any } ? K : never]: T[K] extends { breakdown?: any }
        ? T[K]['breakdown']
        : never
}

export type ScorecardBreakdowns = PickScorecardBreakdowns<AllSections>
export type SectionTypeWithBreakdown = keyof ScorecardBreakdowns

export interface ScorecardBodyWithoutCoverPage {
    overallScore: number
    sections: AllSections
    keyStatistics: KeyStatistics
    performanceMode: Targets.PerformanceMode
}

export interface ScorecardBody extends ScorecardBodyWithoutCoverPage {
    coverPageData: CoverPageData
}

export interface ScorecardListItem {
    id: string
    overallScore: number
    coverPageData: CoverPageData
    sections: {
        type: SectionType
        title: string
        score: number
        noData: boolean
        month?: string // if 'overall' type
        details?: AllDetails
    }[]
    allTimeHigh: boolean
    allTimeLow: boolean
    autoGenerated: boolean
    createdAt: string
    noData?: boolean
}

export interface ScorecardAccountInfo {
    currency: string
    colourPreference: string
    accountId: Account.ID | Domain.ID
}

export interface ScorecardSenderInfo {
    senderEmail: string
    senderName: string
    senderLogoUrl?: string | null
    userId?: number
}

export interface SingleScorecardFull {
    body: ScorecardBody
    overviewScore: number
    createdAt: string
    account: ScorecardAccountInfo
    autoGenerated: boolean
}

export interface CoverPageDataWithoutSender {
    accountName: string | null
    accountLogoUrl: string | null
    industry: string | null
    location: string | null
    website: string | null
}

export interface CoverPageData extends CoverPageDataWithoutSender, ScorecardSenderInfo {}

export type SpendStatus = 'on' | 'above' | 'below' | 'none'

export interface AdGroup {
    ad_group: GadsFieldsRequired<resources.AdGroup, 'id' | 'resource_name' | 'name'>
    campaign: GadsFieldsRequired<resources.AdGroup, 'name' | 'resource_name'>
    metrics: GadsFieldsRequired<common.Metrics, 'cost_micros'>
}

export interface RawCustomerCost {
    metrics: GadsFieldsRequired<common.Metrics, 'cost_micros'>
    segments: GadsFieldsRequired<common.Segments, 'date'>
}

export interface CustomerCost {
    cost: number
    date: string
}

export interface CustomerStats {
    metrics: GadsFieldsRequired<
        common.Metrics,
        'cost_micros' | 'impressions' | 'conversions' | 'conversions_value'
    >
    customer: GadsFieldsRequired<resources.Customer, 'resource_name'>
    segments: GadsFieldsRequired<common.Segments, 'date'>
}

export interface LineChartItem {
    x: number
    y: number
    dotted?: boolean
}

export interface TableData {
    name: string
    metrics: {
        cost?: number
        conversions?: number
        conversions_value?: number
        cpa?: number
        count?: string
    }
}

export interface PieData {
    name: string
    y: number
}

export interface ScorecardRenderData {
    content: ScorecardBody
    domain_name: string
    domain_currency: string
    domain_logo_url: string
    adwords_timezone: string
    user_data: UserData
    domain_color_preference: string
    cover_page: CoverPageData
}

export interface KeyStatistics {
    type: 'key_statistics'
    spend: number
    conversions: number
    conversions_value: number
    cpa: number | null
    roas: number | null
    impressions: number
    spend_delta: number
    conversions_delta: number
    conversions_value_delta: number
    cpa_delta: number | null
    roas_delta: number | null
    impressions_delta: number
}

export interface LandingPage {
    url: string
    clicks: number
    cost: number
    conversions: number
    conversionsValue: number
    impressions: number
    conversionRate: number
    usesHttps: boolean
    mobileFriendlyScore: number | null
    speedScore: number | null
    isRedirect: boolean | null
    isBroken: boolean | null
}

export interface LandingPageSubScores {
    httpsUsageScore: number
    brokenLinkScore: number | null
    redirectScore: number | null
    speedScore: number | null
    mobileFriendlyScore: number | null
}
export interface LandingPagesScoreSectionDetails {
    subScores: LandingPageSubScores
    topPages: LandingPage[]
    totalPageCount: number
    totalBrokenPageCount: number
    totalCostOfNonTopPages: number
}

export interface LandingPagesScoreSection extends Section {
    type: 'landingPages'
    details: LandingPagesScoreSectionDetails
    breakdown?: { allPages: LandingPage[] }
}

export interface AdGroupAlignmentMetrics {
    clicks: number
    cost: number
    impressions: number
    conversions: number
    conversionsValue: number
}

export interface AdGroupAlignmentWord {
    word: string
    metrics: AdGroupAlignmentMetrics
}

export interface STWordMissingInRSA extends AdGroupAlignmentWord {
    searchTermSet: string[]
    adIdSet: string[]
}

export interface STWordMissingInLP extends AdGroupAlignmentWord {
    searchTermSet: string[]
    landingPageSet: string[]
}

export interface RSAWordMissingInLP {
    word: string
    assetSet: string[]
    adIdSet: string[]
    landingPageSet: string[]
}

export interface RSAWordMissingInST {
    word: string
    assetSet: string[]
    adIdSet: string[]
}

export interface LPWordMissingInRSA {
    word: string
    count: number
    adIdSet: string[]
    landingPageSet: string[]
}

export interface LPWordMissingInST {
    word: string
    count: number
    landingPageSet: string[]
}

export interface AlignmentLandingPage {
    normalizedUrl: string
    wordCounts: Record<string, number>
    lastInspectionAt: string
}

export enum AdGroupAlignmentKeywordMatchType {
    Unspecified = 'Unspecified',
    Unknown = 'Unknown',
    Exact = 'Exact',
    Phrase = 'Phrase',
    Broad = 'Broad',
}

export interface AdGroupAlignmentKeyword {
    resourceName: string
    keyword: string
    matchType: AdGroupAlignmentKeywordMatchType
    metrics: AdGroupAlignmentMetrics
}

export interface AdGroupAlignmentTextAsset {
    textAsset: string
    words: string[]
}

export interface AdGroupAlignmentSearchTerm {
    searchTerm: string
    metrics: AdGroupAlignmentMetrics
    words: string[]
}

export interface AdGroupAlignmentAd {
    id: number
    resourceName: string
    landingPageUrl: string
    landingPageNormalizedUrl: string
    textAssets: AdGroupAlignmentTextAsset[]
    searchTerms: AdGroupAlignmentSearchTerm[]
    landingPageScanFailed: boolean
}

export interface AdGroupAlignment {
    campaignName: string
    name: string
    resourceName: string
    metrics: AdGroupAlignmentMetrics & {
        ctr: number
        conversionRate: number
        cpa: number
        roas: number
    }
    searchTermWordsMissingInRsa: STWordMissingInRSA[]
    searchTermWordsMissingInLp: STWordMissingInLP[]
    rsaWordsMissingInLp: RSAWordMissingInLP[]
    rsaWordsMissingInSt: RSAWordMissingInST[]
    lpWordsMissingInRsa: LPWordMissingInRSA[]
    lpWordsMissingInSt: LPWordMissingInST[]
    searchTermToLandingPageAlignment: number
    searchTermToRsaAlignment: number
    rsaToLandingPageAlignment: number
    alignment: number
    landingPageScanFailed: boolean
    ads: AdGroupAlignmentAd[]
}

export interface CampaignAdGroupAlignment {
    name: string
    resourceName: string
    metrics: AdGroupAlignmentMetrics
    adGroups: AdGroupAlignment[]
}

export interface AdGroupAlignmentSubScores {
    searchTermToLandingPageAlignment: number
    searchTermToRsaAlignment: number
    rsaToLandingPageAlignment: number
}
export interface AdGroupAlignmentDetails {
    worstAdGroups: Pick<
        AdGroupAlignment,
        | 'campaignName'
        | 'name'
        | 'resourceName'
        | 'alignment'
        | 'searchTermToLandingPageAlignment'
        | 'searchTermToRsaAlignment'
        | 'rsaToLandingPageAlignment'
    >[]
    subScores: AdGroupAlignmentSubScores
    landingPageScanFailed: boolean
}
export interface AdGroupAlignmentBreakdown {
    alignment: CampaignAdGroupAlignment[]
    landingPages: AlignmentLandingPage[]
}

export interface SearchTermAlignmentSection extends Section {
    type: 'searchTermAlignment'
    details: AdGroupAlignmentDetails
    breakdown?: string // compressed AdGroupAlignmentBreakdown
}

export type DisapprovedEntityType = 'ad' | 'keyword' | Assets.TypeName

export interface DisapprovalReason {
    type: PolicyTopicEntryTypeName
    topic: string
}

export interface DisapprovalLocationDetail {
    name: string
    id: number
}

export type DisapprovalLocation = Improvement.AccountLocation & {
    campaign?: DisapprovalLocationDetail
    adgroup?: DisapprovalLocationDetail
}

export interface DisapprovedEntity {
    id: number
    name?: string
    type: DisapprovedEntityType
    location: DisapprovalLocation
    disapprovalStatus: Assets.PolicyApprovalStatusName
    disapprovalReasons: DisapprovalReason[]
    cost: number
    impressions: number
    openableRsa?: boolean
}

export interface DisapprovalSummary {
    total: number
    disapproved: number
}

export interface DisapprovalsScoreSection extends Section {
    type: 'disapprovals'
    details: {
        summary: {
            ads: DisapprovalSummary
            keywords: DisapprovalSummary
            extensions: DisapprovalSummary
        }
        topDisapprovals: DisapprovedEntity[]
    }
    breakdown?: { allDisapprovals: DisapprovedEntity[] }
}

export interface UserData {
    username: string
    name: string
    profile_image_url: string
}

export interface LiveViewer {
    name: string
    userId: number
    profileImageUrl: string
    seenRecently: boolean
    isYou: boolean
}

export interface QsKeyword {
    keyword_view: GadsFieldsRequired<resources.KeywordView, 'resource_name'>
    ad_group_criterion: GadsFieldsRequired<
        resources.AdGroupCriterion,
        'resource_name' | 'criterion_id' | 'quality_info'
    >
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'name'>
    metrics: GadsFieldsRequired<
        common.Metrics,
        'impressions' | 'cost_micros' | 'all_conversions' | 'all_conversions_value'
    >
}

export interface BudgetCapCampaign {
    campaign: GadsFieldsRequired<
        resources.Campaign,
        | 'resource_name'
        | 'id'
        | 'name'
        | 'bidding_strategy'
        | 'advertising_channel_type'
        | 'manual_cpc'
        | 'target_cpa'
        | 'target_roas'
        | 'maximize_conversions'
        | 'maximize_conversion_value'
    >
    metrics: GadsFieldsRequired<
        common.Metrics,
        | 'cost_micros'
        | 'all_conversions'
        | 'all_conversions_value'
        | 'search_budget_lost_impression_share'
    >
}

export interface GaData {
    campaign: GadsFieldsRequired<resources.Campaign, 'id' | 'resource_name' | 'name'>
    metrics: GadsFieldsRequired<
        common.Metrics,
        | 'bounce_rate'
        | 'percent_new_visitors'
        | 'average_page_views'
        | 'average_time_on_site'
        | 'clicks'
    >
    segments: GadsFieldsRequired<common.Segments, 'date'>
}

export interface NonBrandingKeyword {
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'id'>
    ad_group_criterion: GadsFieldsRequired<resources.AdGroupCriterion, 'keyword' | 'resource_name'>
    metrics: GadsFieldsRequired<
        common.Metrics,
        'cost_micros' | 'conversions' | 'conversions_value' | 'impressions'
    >
}

export type MatchtypeSpreadLabelKeys = 'broad' | 'exact' | 'phrase'

export type KeywordCountLabelKeys = 'one' | 'two' | 'three' | 'fourplus'

export interface SharedNegativeSharedSet {
    shared_set: GadsFieldsRequired<resources.SharedSet, 'id' | 'resource_name'>
    shared_criterion: GadsFieldsRequired<resources.SharedCriterion, 'resource_name'>
}

export interface CustomerConversion {
    customer: GadsFieldsRequired<resources.Customer, 'resource_name'>
    metrics: GadsFieldsRequired<common.Metrics, 'cost_micros' | 'impressions' | 'conversions'>
}

export interface ConversionAction {
    conversion_action: GadsFieldsRequired<
        resources.ConversionAction,
        'resource_name' | 'name' | 'primary_for_goal' | 'origin' | 'type'
    >
    metrics: GadsFieldsRequired<common.Metrics, 'all_conversions' | 'all_conversions_value'>
    segments: GadsFieldsRequired<common.Segments, 'date'>
}

export interface ConversionActionType {
    conversion_action: GadsFieldsRequired<
        resources.ConversionAction,
        'resource_name' | 'name' | 'origin' | 'type'
    >
}

export interface AssetGroupSignal {
    campaign: {
        name: string
        resource_name: string
    }
    asset_group: {
        resource_name: string
        name: string
    }
    asset_group_signal: {
        audience: {
            audience: string
        }
        resource_name: string
    }
}

export interface AssetGroupAsset {
    campaign: {
        name: string
        resource_name: string
    }
    asset_group: {
        name: string
        id: number
        ad_strength: enums.AdStrength
        status: enums.AssetGroupStatus
        resource_name: string
    }
    asset: {
        resource_name: string
        source: enums.AssetSource
        type: enums.AssetType
    }
    asset_group_asset: {
        field_type: enums.AssetFieldType
        resource_name: string
    }
}

export interface AssetViewAsset {
    ad_group_ad_asset_view: GadsFieldsRequired<
        resources.AdGroupAdAssetView,
        'resource_name' | 'asset' | 'field_type' | 'performance_label'
    >
    ad_group_ad: GadsFieldsRequired<resources.AdGroupAd, 'ad'>
    metrics: GadsFieldsRequired<common.Metrics, 'impressions'>
}

export interface Audience {
    audience: {
        description: string
        name: string
        dimensions: common.AudienceDimension[]
        resource_name: string
    }
}

export interface ProductPerformance {
    campaign: {
        name: string
        advertising_channel_type: enums.AdvertisingChannelType
        resource_name: string
        status: enums.CampaignStatus
        experiment_type: enums.CampaignExperimentType
        serving_status: enums.CampaignServingStatus
    }
    metrics: {
        impressions: number
        cost_micros: number
        conversions: number
        conversions_value: number
    }
    segments: {
        product_item_id: string
        product_custom_attribute0: string | null
        product_custom_attribute1: string | null
        product_custom_attribute2: string | null
        product_custom_attribute3: string | null
        product_custom_attribute4: string | null
        product_type_l1: string
        product_type_l2: string
        product_type_l3: string | null
        product_type_l4: string | null
        product_type_l5: string | null
    }
}

export interface PMaxCampaigns {
    campaign: {
        name: string
        advertising_channel_type: enums.AdvertisingChannelType
        resource_name: string
    }
    metrics: {
        impressions: number
        clicks: number
        cost_micros: number
        conversions: number
        conversions_value: number
    }
    segments: {
        date: string
    }
}

export interface PMaxCampaignsByNetwork {
    campaign: {
        name: string
        advertising_channel_type: enums.AdvertisingChannelType
        resource_name: string
    }
    metrics: {
        impressions: number
        cost_micros: number
        conversions: number
        conversions_value: number
    }
    segments: {
        date: string
        ad_network_type: enums.AdNetworkType
    }
}

export interface AssetGroup {
    campaign: {
        name: string
        resource_name: string
    }
    asset_group: {
        name: string
        ad_strength: enums.AdStrength
        status: enums.AssetGroupStatus
        resource_name: string
    }
    asset_group_listing_group_filter: {
        type: enums.ListingGroupFilterType
        resource_name: string
    }
    metrics: {
        impressions: number
        clicks: number
        cost_micros: number
    }
    segments: {
        date: string
    }
}

export interface Asset {
    asset: {
        id: number
        final_urls: []
        source: enums.AssetSource
        type: enums.AssetType
        youtube_video_asset: {
            youtube_video_title: string
            youtube_video_id: string
        }
        text_asset: string
        resource_name: string
        name: string
    }
}

export interface PaidOrganicSearchTermView {
    paid_organic_search_term_view: GadsFieldsRequired<
        resources.PaidOrganicSearchTermView,
        'resource_name' | 'search_term'
    >
    metrics: GadsFieldsRequired<common.Metrics, 'organic_clicks'>
}

export interface AssetSet {
    asset_set: GadsFieldsRequired<resources.AssetSet, 'resource_name' | 'name' | 'type'>
}

export interface SearchTerm {
    search_term_view: GadsFieldsRequired<resources.SearchTermView, 'resource_name' | 'search_term'>
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name'>
    ad_group: GadsFieldsRequired<resources.AdGroup, 'resource_name'>
    metrics: GadsFieldsRequired<common.Metrics, 'conversions'>
}

export interface Experiment {
    experiment: GadsFieldsRequired<resources.Experiment, 'resource_name' | 'type' | 'start_date'>
}

export interface CampaignsSpendMetrics {
    campaignGroupName: string
    campaignGroupId: string
    campaignId: string
    campaignName: string
    cost: number
    clicks: number
    impressions: number
    costPercent: number
    conversions: number
    estimatedConvChange: number
    estimatedConvValueChange: number
    rollingEstimatedConvChange: number
    rollingEstimatedConvValueChange: number
    conversionsPercent: number
    conversionsValue: number
    conversionsValuePercent: number
    cpa: number
    roas: number
    vsAvg: number
    campaignSharedBudgetName: string | null
    searchImpressionsShare: number
    rollingSearchImpressionsShare: number
    shareLostToBudget: number
    shareLostToRank: number
    avgDailySpend: number
    suggestedDailySpend: number
    suggestedDailySpendPercent: number
    dailyBudget: number | null
    isSharedBudget: boolean
    diff: number
    aboveMinClicks: boolean
    isValidCampaign: boolean
}

export interface CampaignWarehouseMetrics {
    totalCostMinis: number
    impressions: number
    clicks: number
    availableImpressions: number
    impressionsLostToBudget: number
    impressionsLostToRank: number
    id: string
    totalConversions: number
    totalValue: number
}
export * from './integrations'
export * from './rsa'
export * from './productPerformance'
